import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { logout } from "../User/State/Auth/Action";
import {
  ADMIN_SESSION_EXPIRED_KEY,
  clearAdminSession,
  isAdminSessionExpired,
  isAdminUser,
  markAdminActivity,
} from "./adminSession";

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];

export const expireAdminSession = (dispatch) => {
  if (!isAdminUser() || !localStorage.getItem("jwt")) {
    return;
  }
  sessionStorage.setItem(ADMIN_SESSION_EXPIRED_KEY, "1");
  dispatch(logout());
  clearAdminSession();
  window.location.replace("/login");
};

export const useAdminSessionGuard = () => {
  const dispatch = useDispatch();
  const lastMarkRef = useRef(0);

  const expire = useCallback(() => {
    expireAdminSession(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (sessionStorage.getItem(ADMIN_SESSION_EXPIRED_KEY)) {
      sessionStorage.removeItem(ADMIN_SESSION_EXPIRED_KEY);
      toast.info("Your session has expired. Please login again.");
    }
  }, []);

  useEffect(() => {
    if (!isAdminUser() || !localStorage.getItem("jwt")) {
      return undefined;
    }

    if (isAdminSessionExpired()) {
      expire();
      return undefined;
    }

    const onActivity = () => {
      const now = Date.now();
      if (now - lastMarkRef.current < 1000) {
        return;
      }
      lastMarkRef.current = now;
      markAdminActivity();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    const intervalId = setInterval(() => {
      if (isAdminSessionExpired()) {
        expire();
      }
    }, 10000);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && isAdminSessionExpired()) {
        expire();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [expire]);
};
